import { OAuthProvider } from '@seapoint/server/enums';
import { FileUp } from 'lucide-react';
import { ComponentProps } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { Button } from '../../design-system/button/button';
import { Inline } from '../../design-system/inline/inline';
import { Stack } from '../../design-system/stack/stack';
import { InvoiceEvidenceView } from '../../types/entity-types';
import { SupportedCountryCurrency } from '../../types/supported-country-currency';
import { ConnectionsAndSettingsSection } from './components/connections-and-settings-section';
import { InvoicesMetricCard, MetricCardVariant } from './components/empty-state/invoices-metric-card';
import { InvoiceDataSection } from './components/invoice-data-section/invoice-data-section';
import { InvoiceDrawerContentBodyVariant } from './components/invoice-drawer/invoice-drawer-content-body';
import { CardVariant, InvoiceScanningProgressCard } from './components/invoice-scanning-progress-card';

type InvoicesMetrics = {
  variant: MetricCardVariant;
  metric: number;
  normalizedAmount: number;
  currency: SupportedCountryCurrency;
};

type EmailScanningProgressConfig =
  | { isVisible: false }
  | {
      isVisible: true;
      status: CardVariant;
      invoiceCount: number;
      onHideCard: () => void;
    };

type InvoicePayPageProps = {
  evidenceDataConfig: ComponentProps<typeof InvoiceDataSection>['evidenceDataConfig'];
  connectionsAndSettingsConfig: ComponentProps<typeof ConnectionsAndSettingsSection>;
  invoicesMetrics: InvoicesMetrics[];
  emailScanningProgressConfig: EmailScanningProgressConfig;
  isFetchingDocumentUrl: boolean;
  selectedEvidenceId?: string;
  approvingEvidenceId?: string[];
  onReviewInvoice: (invoiceId: string, variant: InvoiceDrawerContentBodyVariant) => void;
  onSelectMenuItem: ComponentProps<typeof InvoiceDataSection>['onSelectMenuItem'];
  onDataSorted: (sortedEvidence: InvoiceEvidenceView[]) => void;
  onApproveInvoice: (invoiceIds: string[]) => Promise<void>;
};

export const InvoicePayPage = ({
  evidenceDataConfig,
  connectionsAndSettingsConfig,
  invoicesMetrics,
  emailScanningProgressConfig,
  isFetchingDocumentUrl,
  selectedEvidenceId,
  approvingEvidenceId,
  onReviewInvoice,
  onSelectMenuItem,
  onDataSorted,
  onApproveInvoice,
}: InvoicePayPageProps) => {
  const location = useLocation();
  const navigate = useNavigate();

  const isEmailConnected = !!connectionsAndSettingsConfig.gmailConfig.connectionStatus?.connectionId;
  const onConnectGmail =
    connectionsAndSettingsConfig.gmailConfig.type === OAuthProvider.GMAIL
      ? connectionsAndSettingsConfig.gmailConfig.onConnect
      : () => {};

  return (
    <Stack
      space={10}
      align="stretch"
      className="px-6 pt-4"
      // Needed to trigger a re-render when the connect gmail email is opened from the empty state component
      key={location.search}
    >
      <Inline justify="between" className="h-[42px]">
        <h1 className="text-2xl font-semibold text-black">Invoice Pay</h1>

        <Inline space={2}>
          <ConnectionsAndSettingsSection {...connectionsAndSettingsConfig} />

          <Button
            variant="default"
            iconLeft={<FileUp />}
            onClick={() => {
              navigate('/payments', {
                state: {
                  isFromManualPayInvoicePage: true,
                  action: 'BILL_PAY_UPLOAD_INVOICE',
                },
              });
            }}
          >
            Upload Invoice
          </Button>
        </Inline>
      </Inline>

      <Stack space={3} align="stretch">
        <Inline space={5} wrap="nowrap">
          {invoicesMetrics.map((metric) => (
            <InvoicesMetricCard
              key={metric.variant}
              variant={metric.variant}
              metric={metric.metric}
              normalizedAmount={metric.normalizedAmount}
              currency={metric.currency}
            />
          ))}
        </Inline>

        {emailScanningProgressConfig.isVisible && (
          <InvoiceScanningProgressCard
            variant={emailScanningProgressConfig.status}
            invoiceCount={emailScanningProgressConfig.invoiceCount}
            onHideCard={emailScanningProgressConfig.onHideCard}
          />
        )}
      </Stack>

      <InvoiceDataSection
        evidenceDataConfig={evidenceDataConfig}
        isEmailConnected={isEmailConnected}
        isFetchingDocumentUrl={isFetchingDocumentUrl}
        selectedEvidenceId={selectedEvidenceId}
        approvingEvidenceId={approvingEvidenceId}
        onConnectGmail={onConnectGmail}
        onReviewInvoice={onReviewInvoice}
        onSelectMenuItem={onSelectMenuItem}
        onDataSorted={onDataSorted}
        onApproveInvoice={onApproveInvoice}
      />
    </Stack>
  );
};
